/**
 * In-memory snapshot store for the iOS swipe-back gesture.
 *
 * We clone `#root` (the React app shell) right BEFORE the user navigates
 * forward, keyed by the URL they are leaving. When the user later starts
 * an edge-swipe back, `iosEdgeSwipe.ts` retrieves the clone and renders
 * it underneath the live app shell so the swipe shows the actual previous
 * screen instead of a blank gray plate.
 *
 * Why DOM clone and not html2canvas / dom-to-image
 * ------------------------------------------------
 *  - Clone is synchronous (a single `cloneNode(true)` call) so we can
 *    capture inside the synthetic `pushState` event handler before React
 *    re-renders. Canvas-based libraries are async and would miss the
 *    window: by the time they finish, the DOM already shows the new page.
 *  - Clone is visually identical because the same global stylesheet
 *    applies. No CSS-paint quirks (border-radius bleeding, gradient
 *    rendering, custom fonts) that canvas approximations occasionally
 *    miss.
 *  - Zero new dependencies — keeps the bundle size unchanged.
 *
 * Trade-offs
 * ----------
 *  - The clone is inert DOM (no React event bindings). That is fine
 *    because the clone is purely visual; it is removed the moment the
 *    swipe finishes.
 *  - Live `<img>` elements re-attach on the clone but are served from
 *    cache (browsers de-dupe by src), so no network re-fetch.
 *
 * Scope guarantees
 * ----------------
 *  - LRU-capped at MAX_SNAPSHOTS entries — the store cannot grow
 *    unbounded.
 *  - Every public function is wrapped in try/catch and degrades to a
 *    no-op on failure, so a broken snapshot can never block navigation.
 *  - No global state beyond the module-private `Map`.
 */

const MAX_SNAPSHOTS = 5;

const store = new Map<string, HTMLElement>();

/**
 * Capture a snapshot of the current `#root` keyed by the URL the user
 * is currently viewing. Call this RIGHT BEFORE navigating away — typically
 * from inside the synthetic `pushState` event handler, where the DOM
 * still reflects the page being left.
 */
export function capturePageSnapshot(url: string): void {
  if (typeof document === "undefined") return;
  if (!url) return;
  try {
    const root = document.getElementById("root");
    if (!root) return;
    const clone = root.cloneNode(true) as HTMLElement;
    // The clone is purely visual — strip anything that could grab focus
    // or accept input while it is briefly attached during the swipe.
    clone.querySelectorAll("input, textarea, select, button, [tabindex]").forEach((el) => {
      try {
        (el as HTMLElement).setAttribute("tabindex", "-1");
      } catch {
        /* ignore */
      }
    });
    // Evict any previous snapshot for the same URL, then LRU-cap.
    store.delete(url);
    if (store.size >= MAX_SNAPSHOTS) {
      const oldest = store.keys().next().value;
      if (oldest !== undefined) store.delete(oldest);
    }
    store.set(url, clone);
  } catch {
    /* never throw — snapshots are best-effort */
  }
}

/** Read (without removing) the snapshot for a URL, or `null` if none. */
export function getPageSnapshot(url: string): HTMLElement | null {
  if (!url) return null;
  return store.get(url) ?? null;
}

/** Drop the snapshot for a URL (called once a swipe commits). */
export function dropPageSnapshot(url: string): void {
  if (!url) return;
  store.delete(url);
}

/** Test-only: clear the whole store. */
export function _clearAllPageSnapshots(): void {
  store.clear();
}

/** Test-only: current store size. */
export function _pageSnapshotCount(): number {
  return store.size;
}

/** Test-only: the LRU cap (so the test can assert eviction at the boundary). */
export const _MAX_SNAPSHOTS_FOR_TEST = MAX_SNAPSHOTS;
